import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, Title } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoScope, TranslocoService } from '@jsverse/transloco';
import { EMPTY, Observable, catchError, forkJoin, of, switchMap, take, tap } from 'rxjs';
import { DeightonStyleWrapperComponent } from '@deighton/shared-ui/core';
import { translocoConf } from '@deighton/shared-ui/services';
import { createInlineLoader } from '../providers/translations';

interface DocumentMeta {
  lang: string;
  title: string;
  description: string;
}

/** Root component for the licensing application. */
@Component({
  selector: 'd-licensing',
  imports: [RouterOutlet, DeightonStyleWrapperComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // The `appMeta.*` keys live in the licensing app's own i18n files, which are
    // only registered under the `licensing` scope. The root component sits above
    // the router-outlet (where route-level scopes are provided), so it must
    // declare the scope itself to resolve those keys.
    {
      provide: TRANSLOCO_SCOPE,
      useValue: {
        scope: 'licensing',
        loader: createInlineLoader(translocoConf.availableLangs as Array<string>)
      }
    }
  ]
})
export class AppComponent implements OnInit {
  private readonly translocoService: TranslocoService = inject(TranslocoService);
  private readonly translocoScope: TranslocoScope = inject(TRANSLOCO_SCOPE);
  private readonly titleService: Title = inject(Title);
  private readonly metaService: Meta = inject(Meta);
  private readonly document: Document = inject(DOCUMENT);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /**
   * Applies the localised document `<title>`, `<meta name="description">`, and
   * `<html lang>` attributes on initial load and whenever the active language
   * changes. The English strings in `index.html` serve as the pre-bootstrap
   * fallback (crawlers, browser tab text before JS executes); Transloco's
   * `appMeta.title` / `appMeta.description` keys win once the app has booted.
   *
   * The keys are resolved against the `licensing` scope (provided on this
   * component) via `selectTranslate`, which defers emission until the scope's
   * translation file has loaded — `translate` would otherwise return the raw
   * key, and a global-scope lookup would never find these scoped keys at all.
   */
  public ngOnInit(): void {
    this.translocoService.langChanges$
      .pipe(
        switchMap(
          (lang: string): Observable<DocumentMeta> =>
            // Load the global shared-ui translations (header, navigation menu, etc.)
            // before resolving the scoped document meta. `resolveDocumentMeta` uses a
            // scoped `selectTranslate`, which merges the `licensing` scope into the
            // active-language bucket and marks it as loaded — that would otherwise stop
            // Transloco from ever fetching the global translation file, leaving shared
            // component keys (`components.*`) untranslated.
            this.translocoService.load(lang).pipe(
              switchMap((): Observable<DocumentMeta> => this.resolveDocumentMeta(lang)),
              // Isolate failures to the current emission so a single failed
              // translation load can't terminate the langChanges$ subscription.
              catchError((error: unknown): Observable<never> => {
                console.error('Failed to resolve localised document meta', error);
                return EMPTY;
              })
            )
        ),
        tap((meta: DocumentMeta): void => {
          this.titleService.setTitle(meta.title);
          this.metaService.updateTag({ name: 'description', content: meta.description });
          this.document.documentElement.lang = meta.lang;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Resolves the localised document meta for the active language, waiting for
   * the `licensing` scope's translation file to load before emitting.
   */
  private resolveDocumentMeta(lang: string): Observable<DocumentMeta> {
    return forkJoin({
      lang: of(lang),
      title: this.translocoService
        .selectTranslate<string>('appMeta.title', {}, this.translocoScope)
        .pipe(take(1)),
      description: this.translocoService
        .selectTranslate<string>('appMeta.description', {}, this.translocoScope)
        .pipe(take(1))
    });
  }
}
